import { Box, Button, CircularProgress, Divider, Modal, Stack, Typography } from "@mui/material";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import "./translations";

import { SaveIcon } from "@/assets";
import { CloseIcon } from "@/assets";

const styles = {
  modalContainer: {
    width: "600px",
    bgcolor: "background.paper",
    borderRadius: 2,
    outline: "none",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "30px",
  },
  headerText: {
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "30px",
    color: "#333333",
  },
  headerIcon: {
    color: "primary.main",
    fontSize: "24px",
  },
  closeIcon: {
    cursor: "pointer",
    fontSize: "18px",
  },
  contentText: {
    fontWeight: 400,
    fontSize: "18px",
    lineHeight: "28px",
    color: "#333333",
    padding: "30px",
  },
};

interface CloseRefineRealEstateModalProps {
  open: boolean;
  onCancel: () => void;
  onSaveAndExit: () => void;
  onDiscardAndExit: () => void;
  onApplyChanges: () => void;
  canApplyChanges: boolean;
  isValuationAction: boolean;
  isSubmitting?: boolean;
  variant: "save" | "discard";
}

export const CloseRefineRealEstateModal = ({
  open,
  onCancel,
  onSaveAndExit,
  onDiscardAndExit,
  onApplyChanges,
  canApplyChanges,
  isValuationAction,
  isSubmitting = false,
  variant,
}: CloseRefineRealEstateModalProps): JSX.Element => {
  const { t } = useTranslation("RealEstateRefinement");
  const { t: tRealEstate } = useTranslation("Common", { keyPrefix: "realEstate" });
  const isDiscardVariant = variant === "discard";

  return (
    <Modal open={open} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Box sx={styles.modalContainer} data-cy="close-refine-modal">
        <Box sx={styles.header}>
          <Stack direction="row" spacing={"15px"} alignItems="center">
            <SaveIcon sx={styles.headerIcon} />
            <Typography sx={styles.headerText}>
              {t(isDiscardVariant ? "closeRefineRealEstateModal.discardTitle" : "closeRefineRealEstateModal.title")}
            </Typography>
          </Stack>
          <CloseIcon sx={styles.closeIcon} onClick={isSubmitting ? undefined : onCancel} color="primary" />
        </Box>
        <Divider />
        <Typography sx={styles.contentText}>
          {isDiscardVariant ? (
            t("closeRefineRealEstateModal.discardText")
          ) : (
            <>
              <strong style={{ display: "block", paddingBottom: "30px" }}>
                {t("closeRefineRealEstateModal.prompt")}
              </strong>
              {t("closeRefineRealEstateModal.text")}
            </>
          )}
        </Typography>
        <Box sx={{ padding: "30px" }}>
          <Stack spacing={"30px"} direction="row" justifyContent="end" sx={{ height: "45px" }}>
            <Button
              onClick={isDiscardVariant ? onDiscardAndExit : onCancel}
              color="secondary"
              variant="contained"
              disabled={isSubmitting}
              data-cy={isDiscardVariant ? "close-refine-modal-confirm" : "close-refine-modal-cancel"}
            >
              {t(
                isDiscardVariant
                  ? "closeRefineRealEstateModal.discardConfirmButton"
                  : "closeRefineRealEstateModal.cancelButton",
              )}
            </Button>
            <Button
              autoFocus
              onClick={isDiscardVariant ? onApplyChanges : onSaveAndExit}
              variant="contained"
              color="primary"
              disabled={isSubmitting || (isDiscardVariant && !canApplyChanges)}
              data-cy={isDiscardVariant ? "close-refine-modal-valuate" : "close-refine-modal-confirm"}
              endIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
            >
              {isDiscardVariant
                ? tRealEstate(isValuationAction ? "dataVerification.implementValuation" : "dataVerification.applyData")
                : t("closeRefineRealEstateModal.confirmButton")}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};
